import moment from "moment";
import { type OptionDto } from "../../../autogen/bff-api";

export const DEADLINE_SOON_DAYS = 90;

export type ExtensionStatus =
  | { kind: "exercised"; at?: string | null; by?: string | null }
  | { kind: "declined"; at?: string | null; by?: string | null }
  | { kind: "lapsed" }
  | { kind: "deadlinePassed" }
  | { kind: "deadlineSoon"; days: number }
  | { kind: "undecided" };

export const daysFromToday = (date: string, today: Date): number =>
  moment(date).startOf("day").diff(moment(today).startOf("day"), "days");

export const extensionStatus = (option: OptionDto, today: Date): ExtensionStatus => {
  if (option.isExecuted) return { kind: "exercised", at: option.decidedAt, by: option.decidedByName };
  if (option.isDeclined) return { kind: "declined", at: option.decidedAt, by: option.decidedByName };
  if (option.isLapsed) return { kind: "lapsed" };
  if (!option.deadline) return { kind: "undecided" };
  const days = daysFromToday(option.deadline, today);
  if (days < 0) return { kind: "deadlinePassed" };
  if (days <= DEADLINE_SOON_DAYS) return { kind: "deadlineSoon", days };
  return { kind: "undecided" };
};

export const isDecided = (option: OptionDto): boolean =>
  !!(option.isExecuted || option.isDeclined || option.isLapsed);

export const extendsContract = (option: OptionDto): boolean => !option.isDeclined && !option.isLapsed;

export interface ContractEnds {
  endsNow?: string;
  endsWithAllExtensions?: string;
  nextDeadline?: string;
}

export const contractEnds = (options: OptionDto[], endDate: string | null | undefined): ContractEnds => {
  const remainingEnds = options
    .filter((option) => extendsContract(option) && !option.isExecuted && option.endDate)
    .map((option) => option.endDate as string);
  const latest = [endDate, ...remainingEnds]
    .filter((date): date is string => !!date)
    .reduce<string | undefined>((max, date) => (!max || moment(date).isAfter(max) ? date : max), undefined);
  return {
    endsNow: endDate ?? undefined,
    endsWithAllExtensions: latest,
    nextDeadline: earliestUndecidedDeadline(options),
  };
};

const earliestUndecidedDeadline = (options: OptionDto[]): string | undefined =>
  options
    .filter((option) => !isDecided(option) && option.deadline)
    .map((option) => option.deadline as string)
    .reduce<string | undefined>((min, date) => (!min || moment(date).isBefore(min) ? date : min), undefined);

export const reachesInto = (option: OptionDto, declined: OptionDto | undefined): boolean =>
  !!declined?.startDate &&
  [option.startDate, option.endDate].some((date) => !!date && !moment(date).isBefore(declined.startDate));

export const firstDeclined = (options: OptionDto[]): OptionDto | undefined =>
  inPeriodOrder(options).find((option) => option.isDeclined && option.startDate);

export const lapsingWith = (options: OptionDto[], declined: OptionDto): OptionDto[] => {
  const ordered = inPeriodOrder(options);
  const position = ordered.findIndex((option) => option.id === declined.id);
  return ordered.filter(
    (option, index) =>
      index === position || (!isDecided(option) && (index > position || reachesInto(option, declined)))
  );
};

export const inPeriodOrder = (options: OptionDto[]): OptionDto[] => {
  const datedByPeriod = options
    .filter((option) => option.startDate)
    .sort((a, b) => byDate(a.startDate, b.startDate) || byDate(a.deadline, b.deadline));
  let next = 0;
  return options.map((option) => (option.startDate ? datedByPeriod[next++] : option));
};

const byDate = (a: string | null | undefined, b: string | null | undefined): number => {
  if (a && b) return moment(a).diff(moment(b));
  if (a) return -1;
  if (b) return 1;
  return 0;
};
